import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const AboutPage = () => {
  const [missionRef, missionInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [teamRef, teamInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [valuesRef, valuesInView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <div className="bg-white min-h-screen pt-32 pb-20">
      {/* Hero */}
      <div className="container mx-auto px-4 max-w-4xl text-center mb-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-block px-4 py-1.5 bg-purple-100 text-purple-700 text-sm font-medium rounded-full mb-4">
            About Qupid
          </span>
          <h1 className="text-5xl font-bold mb-6 text-gray-900">
            Born from the data teams <br />that built enterprise data infrastructure
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Qupid was created by a team of data engineers and consultants who spent years
            building data platforms for leading enterprises and regulated industries.
            We built the tool we wished we had.
          </p>
        </motion.div>
      </div>

      {/* Origin Story */}
      <section className="py-16 bg-gray-50" ref={missionRef}>
        <div className="container mx-auto px-4 max-w-5xl">
          <motion.div
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
            initial={{ opacity: 0, y: 20 }}
            animate={missionInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <div>
              <h2 className="text-3xl font-bold mb-6 text-gray-900">Our Story</h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  Qupid is backed by a leading data consulting company — a team
                  that has spent years designing, building, and managing data platforms for
                  large financial institutions and enterprises.
                </p>
                <p>
                  Through hundreds of consulting engagements, we saw the same problem everywhere:
                  data teams spending more time fighting data quality fires than creating value.
                  Dashboards nobody trusted. Reports that broke silently. Regulatory submissions
                  that required days of manual validation.
                </p>
                <p>
                  We built Qupid to solve this. A complete data observability platform designed
                  from day one for the realities of regulated industries: on-premise deployment,
                  data sovereignty, and the complexity of real enterprise data infrastructure.
                </p>
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-2xl p-8">
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center p-4">
                  <p className="text-4xl font-bold text-purple-600 mb-1">8+</p>
                  <p className="text-sm text-gray-500">Engineers on the team</p>
                </div>
                <div className="text-center p-4">
                  <p className="text-4xl font-bold text-purple-600 mb-1">30+</p>
                  <p className="text-sm text-gray-500">Built-in observers</p>
                </div>
                <div className="text-center p-4">
                  <p className="text-4xl font-bold text-purple-600 mb-1">15+</p>
                  <p className="text-sm text-gray-500">Supported integrations</p>
                </div>
                <div className="text-center p-4">
                  <p className="text-4xl font-bold text-purple-600 mb-1">1yr</p>
                  <p className="text-sm text-gray-500">Dedicated R&D</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Team Expertise */}
      <section className="py-16" ref={teamRef}>
        <div className="container mx-auto px-4 max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={teamInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold mb-4 text-gray-900 text-center">
              Deep Expertise Where It Matters
            </h2>
            <p className="text-lg text-gray-600 text-center max-w-2xl mx-auto mb-12">
              Our team combines consulting experience at top data-driven organizations
              with product engineering expertise.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold mb-2 text-gray-900">Enterprise Data Infrastructure</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Years of experience designing and managing data platforms for
                  large enterprises — risk systems, regulatory reporting, customer analytics.
                </p>
              </div>
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold mb-2 text-gray-900">Product Engineering</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  A dedicated 8-person engineering team building Qupid full-time.
                  AI/ML capabilities, 30+ observers, BI Discovery, and advanced lineage.
                </p>
              </div>
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold mb-2 text-gray-900">Regulated Industries</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  We understand the constraints — on-premise requirements, data sovereignty,
                  compliance frameworks, and the operational reality of enterprise IT.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 bg-gray-50" ref={valuesRef}>
        <div className="container mx-auto px-4 max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={valuesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold mb-12 text-gray-900 text-center">What We Believe</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {[
                {
                  title: 'Data trust is earned, not assumed',
                  description: 'Every number in a dashboard should be traceable to its source. If you can\'t prove your data is correct, it isn\'t.'
                },
                {
                  title: 'On-premise is not a limitation',
                  description: 'For regulated industries, deploying on-premise is a feature, not a constraint. We build for that reality.'
                },
                {
                  title: 'Observability should be proactive',
                  description: 'Finding data issues after a report breaks is too late. You should know about problems before your business does.'
                },
                {
                  title: 'Local expertise matters',
                  description: 'Understanding the market, the regulations, and the people is as important as the technology. We\'re here, not in San Francisco.'
                }
              ].map((value, index) => (
                <div key={index} className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mt-1">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">{value.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{value.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-3xl text-center">
          <h2 className="text-3xl font-bold mb-4 text-gray-900">Ready to see Qupid in action?</h2>
          <p className="text-lg text-gray-600 mb-8">
            Schedule a demo with our team and see how data observability works
            for enterprise environments.
          </p>
          <a href="/#request-demo" className="button button-primary inline-block">
            Request a Demo
          </a>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
